import { socket } from '../../core/network/socket';

/**
 * Client-side mirror of backend/src/game/matchmaking/matchmaking.constants.ts
 * and backend/src/game/hub/hub.constants.ts event-name strings — hand-synced,
 * same convention as config/character-roster.ts. Both systems share the
 * app's one Socket.io gateway (`core/network/socket.ts`'s `socket` singleton,
 * already connected by main.ts before either the hub screen or a match
 * mounts), so this one file covers both wire protocols.
 */
export const MATCHMAKING_EVENTS = {
  JOIN_QUEUE: 'matchmaking.join_queue',
  LEAVE_QUEUE: 'matchmaking.leave_queue',
  QUEUED: 'matchmaking.queued',
  FOUND: 'matchmaking.found',
  LEFT: 'matchmaking.left',
  /** Added 2026-08-20 — design/gdd/matchmaking.md Core Rule #6a. */
  READY_CHECK: 'matchmaking.ready_check',
  READY_ACCEPT: 'matchmaking.ready_accept',
  READY_DECLINE: 'matchmaking.ready_decline',
  READY_CHECK_FAILED: 'matchmaking.ready_check_failed',
} as const;

/** Added 2026-08-20 — design/gdd/matchmaking.md squad-size queue modes. */
export const PARTY_EVENTS = {
  INVITE: 'party.invite',
  ACCEPT: 'party.accept',
  DECLINE: 'party.decline',
  LEAVE: 'party.leave',
  INVITE_RECEIVED: 'party.invite_received',
  MEMBER_JOINED: 'party.member_joined',
  MEMBER_LEFT: 'party.member_left',
  DISBANDED: 'party.disbanded',
  QUEUE_CANCELLED: 'party.queue_cancelled',
} as const;

export const HUB_EVENTS = {
  MEMBER_JOINED: 'hub.member_joined',
  MEMBER_LEFT: 'hub.member_left',
} as const;

/** design/gdd/pvp-arena.md Core Rule #3 — ARENA_SOLO/DUO/TRIO reuse the exact same queue/party/Ready-Check wire protocol as PvE's squad modes. */
export type SquadMode =
  | 'SOLO' | 'DUO' | 'TRIO'
  | 'ARENA_SOLO' | 'ARENA_DUO' | 'ARENA_TRIO'
  | 'WAVES_SOLO' | 'WAVES_DUO' | 'WAVES_TRIO';
export type PartyMode = 'DUO' | 'TRIO' | 'ARENA_DUO' | 'ARENA_TRIO';

export interface MatchFoundPayload {
  roomId: string;
  reservation: unknown;
  /** The queue this seat was found for (2026-10-16). Optional so an older server still works. */
  mode?: SquadMode;
}

export interface PartyInviteReceivedPayload {
  partyId: string;
  mode: PartyMode;
  fromUserId: string;
  fromUsername: string;
}

/** Full party snapshot, sent to every current member (including the one who just joined). */
export interface PartyMemberJoinedPayload {
  partyId: string;
  mode: PartyMode;
  leaderId: string;
  memberIds: string[];
  joinedUserId: string;
}

export interface PartyMemberLeftPayload {
  partyId: string;
  userId: string;
}

export interface ReadyCheckPayload {
  checkId: string;
  /** The server's own wall clock. Do NOT count down against `Date.now()` — see `timeoutMs`. */
  deadline: number;
  /**
   * How long the check lasts, from the moment it was emitted (2026-09-08).
   *
   * The countdown is anchored on this and the client's clock at receipt,
   * because `deadline` is a timestamp from the *server's* clock and browsers
   * are not obliged to agree with it. Optional so a client can outlive a
   * server that predates the field.
   */
  timeoutMs?: number;
  roster: { username: string }[];
  /** The queue this check is for (2026-10-16). */
  mode?: SquadMode;
}

export interface ReadyCheckFailedPayload {
  reason: 'declined' | 'timeout';
}

/** design/gdd/character-creation.md — amended 2026-08-20 from characterClass; the server loads the Character authoritatively. */
export function joinQueue(mode: SquadMode, characterId: string): void {
  socket.emit(MATCHMAKING_EVENTS.JOIN_QUEUE, { mode, metadata: { characterId } });
}

export function leaveQueue(): void {
  socket.emit(MATCHMAKING_EVENTS.LEAVE_QUEUE);
}

export function invitePartyMember(targetUserId: string, mode: PartyMode): void {
  socket.emit(PARTY_EVENTS.INVITE, { targetUserId, mode });
}

export function acceptPartyInvite(partyId: string): void {
  socket.emit(PARTY_EVENTS.ACCEPT, { partyId });
}

export function declinePartyInvite(partyId: string): void {
  socket.emit(PARTY_EVENTS.DECLINE, { partyId });
}

export function leaveParty(): void {
  socket.emit(PARTY_EVENTS.LEAVE);
}

export function acceptReadyCheck(checkId: string): void {
  socket.emit(MATCHMAKING_EVENTS.READY_ACCEPT, { checkId });
}

export function declineReadyCheck(checkId: string): void {
  socket.emit(MATCHMAKING_EVENTS.READY_DECLINE, { checkId });
}
